// AddressPicker.tsx
import React, { useRef, useCallback, memo } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF as Marker,
  Autocomplete,
} from "@react-google-maps/api";
import BoxDX from "../layout/boxdx";
import TextFieldDX from "../controls/textfielddx";
import { IconButton, InputAdornment } from "@mui/material";
import { MyLocation, Search } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useConfigContext } from "../../context/configcontext";

const containerStyle = { width: "100%", height: "400px" };
const DEFAULT_ZOOM = 16;
const GOOGLE_MAP_LIBRARIES: "places"[] = ["places"];
interface Props {
  location: { lat?: number; lng?: number; streetAddress: string };
  setLocation: (loc: {
    lat: number;
    lng: number;
    streetAddress: string;
  }) => void;
  onAddressChange: () => void;
  emptyAddressError?: string;
}

// helper to pull out a component by type
function getComp(comps: google.maps.GeocoderAddressComponent[], type: string) {
  const c = comps.find((c) => c.types.includes(type));
  return c ? c.long_name : "";
}

const AddressPicker: React.FC<Props> = ({
  location,
  setLocation,
  onAddressChange,
  emptyAddressError,
}) => {
  const { t } = useTranslation();
  const { googleMapsApiKey } = useConfigContext();
  const mapRef = useRef<google.maps.Map>();
  const autoRef = useRef<google.maps.places.Autocomplete>();
  const placesServiceRef = useRef<google.maps.places.PlacesService>();
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: googleMapsApiKey,
    libraries: GOOGLE_MAP_LIBRARIES,
  });

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    placesServiceRef.current = new window.google.maps.places.PlacesService(map);
  }, []);

  const onMapUnmount = useCallback(() => {
    mapRef.current = undefined;
    placesServiceRef.current = undefined;
  }, []);

  // fallback geocode for arbitrary text
  const geocodeAddress = (text: string) => {
    if (!text) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: text }, (results, status) => {
      if (status !== "OK" || !results?.length) return;
      const res = results[0];
      const lat = res.geometry.location!.lat();
      const lng = res.geometry.location!.lng();

      // build a one-line from components
      const comps = res.address_components || [];
      const street = [getComp(comps, "route"), getComp(comps, "street_number")]
        .filter(Boolean)
        .join(" ");
      const subloc =
        getComp(comps, "sublocality_level_1") || getComp(comps, "neighborhood");
      const city = getComp(comps, "locality");
      const postal = getComp(comps, "postal_code");
      const country = getComp(comps, "country");
      const fallback = [
        street,
        subloc,
        [city, postal].filter(Boolean).join(" "),
        country,
      ]
        .filter(Boolean)
        .join(", ");

      mapRef.current!.panTo({ lat, lng });
      mapRef.current!.setZoom(DEFAULT_ZOOM);
      setLocation({ lat, lng, streetAddress: fallback });
      onAddressChange();
    });
  };

  // user clicks dropdown or Search icon
  const onPlaceChanged = () => {
    const ac = autoRef.current;
    const svc = placesServiceRef.current;
    if (!ac) {
      // autocomplete not ready
      return;
    }

    const place = ac.getPlace?.() ?? null;
    // if we got a valid PlaceResult with an ID & geometry, do the details call
    if (place && place.place_id && place.geometry && svc) {
      svc.getDetails(
        {
          placeId: place.place_id,
          fields: [
            "name",
            "formatted_address",
            "geometry",
            "address_component",
          ],
        },
        (details, status) => {
          if (status !== "OK" || !details?.geometry) return;

          const name = details.name ?? "";
          const formatted = details.formatted_address ?? "";
          const lat = details.geometry.location!.lat();
          const lng = details.geometry.location!.lng();

          mapRef.current!.panTo({ lat, lng });
          mapRef.current!.setZoom(DEFAULT_ZOOM);
          setLocation({
            lat,
            lng,
            streetAddress:
              name && formatted ? `${name}, ${formatted}` : formatted,
          });
          onAddressChange();
        }
      );
    } else {
      // no place_id or no geometry → treat current text as free-form
      geocodeAddress(location.streetAddress);
    }
  };

  // User clicks on the map (maybe on a POI)
  const handleMapClick = (e: any) => {
    if (e.placeId) {
      // clicked a POI icon
      e.stop(); // prevent default infoWindow
      fetchPlaceDetails(e.placeId, e.latLng!);
    } else {
      // plain click: move marker and reverse geocode
      handleMarkerDragEnd(e);
    }
  };

  // Fetch full details (name + formatted_address) for a placeId
  const fetchPlaceDetails = (placeId: string, latLng: google.maps.LatLng) => {
    const svc = placesServiceRef.current;
    if (!svc) return;

    svc.getDetails(
      {
        placeId,
        fields: ["name", "formatted_address", "geometry", "address_component"],
      },
      (details, status) => {
        if (status !== "OK" || !details || !details.geometry) return;

        const name = details.name ?? "";
        const formatted = details.formatted_address ?? "";
        const lat = details.geometry.location!.lat();
        const lng = details.geometry.location!.lng();

        const humanAddress =
          name && formatted ? `${name}, ${formatted}` : formatted;

        mapRef.current!.panTo({ lat, lng });
        mapRef.current!.setZoom(DEFAULT_ZOOM);
        setLocation({ lat, lng, streetAddress: humanAddress });
        onAddressChange();
      }
    );
  };

  // User drags the marker or clicks non-POI: reverse‐geocode only
  const handleMarkerDragEnd = async (e: google.maps.MapMouseEvent) => {
    const lat = e.latLng!.lat();
    const lng = e.latLng!.lng();
    setLocation({ lat, lng, streetAddress: location.streetAddress });
    onAddressChange();

    const geocoder = new window.google.maps.Geocoder();
    const { results } = await geocoder.geocode({ location: { lat, lng } });
    if (!results[0]?.address_components) return;

    const comps = results[0].address_components;
    const street = [getComp(comps, "route"), getComp(comps, "street_number")]
      .filter(Boolean)
      .join(" ");
    const subloc =
      getComp(comps, "sublocality_level_1") || getComp(comps, "neighborhood");
    const city = getComp(comps, "locality");
    const postal = getComp(comps, "postal_code");
    const country = getComp(comps, "country");

    const humanAddress = [
      street,
      subloc,
      [city, postal].filter(Boolean).join(" "),
      country,
    ]
      .filter(Boolean)
      .join(", ");

    setLocation({ lat, lng, streetAddress: humanAddress });
    onAddressChange();
  };

  return (
    <BoxDX display="flex" flexDirection="column" width="100%">
      {isLoaded && (
        <Autocomplete
          onLoad={(auto) => (autoRef.current = auto)}
          onPlaceChanged={onPlaceChanged}
          options={{
            fields: [
              "place_id",
              "name",
              "formatted_address",
              "geometry",
              "address_component",
            ],
          }}
        >
          <TextFieldDX
            label={t("Street Address")}
            name="streetAddress"
            value={location.streetAddress}
            onChange={(e: { target: { value: any } }) => {
              onAddressChange();
              setLocation({
                lat: location.lat ?? 0,
                lng: location.lng ?? 0,
                streetAddress: e.target.value,
              });
            }}
            error={emptyAddressError}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={onPlaceChanged}>
                    <Search />
                  </IconButton>
                  <IconButton
                    onClick={() =>
                      navigator.geolocation.getCurrentPosition(({ coords }) => {
                        const { latitude: lat, longitude: lng } = coords;
                        setLocation({
                          lat,
                          lng,
                          streetAddress: location.streetAddress,
                        });
                        onAddressChange();
                        // reuse your existing drag-handler signature
                        handleMarkerDragEnd({
                          latLng: new window.google.maps.LatLng(lat, lng),
                        } as any);
                      })
                    }
                  >
                    <MyLocation />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Autocomplete>
      )}

      {isLoaded && location.lat != null && location.lng != null && (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={{ lat: location.lat, lng: location.lng }}
          zoom={DEFAULT_ZOOM}
          onLoad={onMapLoad}
          onUnmount={onMapUnmount}
          onClick={handleMapClick}
          options={{ clickableIcons: true }}
        >
          <Marker
            position={{ lat: location.lat, lng: location.lng }}
            draggable
            onDragEnd={handleMarkerDragEnd}
            zIndex={10000}
          />
        </GoogleMap>
      )}
    </BoxDX>
  );
};

export default memo(AddressPicker);
